import { useCallback, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { INTL_SHORT_TIME_FORMAT_24_HOUR } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import IconButton from 'LuxKit/Button/IconButton'
import TextLink from 'LuxKit/TextLink'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import LineEllipsisVIcon from 'LuxKit/Icons/line/LineEllipsisVIcon'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import Sheet from 'components/utils/Sheet'
import ResponsiveDropdownList from 'tripPlanner/components/Common/Dropdowns/ResponsiveDropdownList'
import BookedLabel from 'tripPlanner/components/BookedLabel'
import { CurrencyProvider } from 'contexts/currencyContext'
import { type FlightCardSavedItemData, type TransportConnection, type TransportOption, isFlightCardSavedItemData, isLeFlightSavedItemData } from 'tripPlanner/api/types'
import { useAirlineByCode } from 'tripPlanner/hooks/api/flights'
import FlightSummaryRow from 'tripPlanner/components/Chat/Flights/FlightSummaryRow'
import { isBookedFlightOption } from './ShortlistedFlightsSection/flightShortlistUtils'
import { getCachedJourney } from './ShortlistedFlightsSection/shortlistedJourneyCache'
import useViewBookedShortlistedFlight from './ShortlistedFlightsSection/useViewBookedShortlistedFlight'
import { dayOffset } from './bookedFlightTileUtils'

const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  width: 100%;
`

const Card = styled(Sheet)`
  container-name: chat-flight-card;
  container-type: inline-size;
  display: flex;
  align-items: stretch;
  min-width: 0;

  &&:hover {
    border-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }
`

const CardContent = styled.button`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: ${rem(12)};
  align-items: center;
  flex: 1;
  min-width: 0;
  padding: ${rem(12)};
  border: 0;
  background: transparent;
  text-align: start;
  color: inherit;
  cursor: pointer;
`

const CardPrice = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;
`

const CardMenu = styled.div`
  display: flex;
  align-items: center;
  padding-inline-end: ${rem(8)};
`

const MoreLink = styled(TextLink)`
  align-self: flex-start;
`

function formatFlightClock(value: string | undefined, intl: ReturnType<typeof useIntl>): string | undefined {
  const match = value?.match(/(?:^|[T\s])(\d{1,2}):(\d{2})/)
  if (!match) return undefined
  const time = new Date(2000, 0, 1, Number(match[1]), Number(match[2]))
  return intlDateFormatter(time, INTL_SHORT_TIME_FORMAT_24_HOUR, intl.locale)
}

interface TimelineFlight {
  option: TransportOption
  savedData: FlightCardSavedItemData
}

function getTimelineOptions(transport: TransportConnection, reversed: boolean): Array<TimelineFlight> {
  const options = transport.options.flatMap((option): Array<TimelineFlight> => {
    const savedData = reversed ? option.returnLeg?.savedItemData : option.savedItemData
    if (!isFlightCardSavedItemData(savedData) || (reversed && !isBookedFlightOption(option))) return []
    return [{ option, savedData }]
  })
  if (reversed) return options
  return [
    ...options.filter(({ option }) => isBookedFlightOption(option)),
    ...options.filter(({ option }) => !isBookedFlightOption(option)),
  ]
}

interface CardProps {
  option: TransportOption
  savedData: FlightCardSavedItemData
  disabled: boolean
  onOpen: (option: TransportOption) => void
  onDelete: (itemId: number) => void
}

function TimelineFlightCard({ option, savedData, disabled, onOpen, onDelete }: CardProps) {
  const intl = useIntl()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const menuTriggerRef = useRef<HTMLButtonElement>(null)
  const { data: airline } = useAirlineByCode({ code: savedData.airlineCode })
  const logo = savedData.carrierLogo || airline?.airlineBrandMark || airline?.airlineLogo
  const departureTime = formatFlightClock(savedData.departureTime, intl)
  const arrivalTime = formatFlightClock(savedData.arrivalTime, intl)
  const route = savedData.departureAirport && savedData.arrivalAirport
    ? `${savedData.departureAirport}-${savedData.arrivalAirport}`
    : undefined
  const layovers = savedData.layovers
  const cachedJourney = savedData.journeyKey ? getCachedJourney(savedData.journeyKey)?.journey : undefined
  // An absent layover list does not prove the flight is direct.
  const numberOfStops = cachedJourney?.flightGroup.numberOfStops ?? layovers?.length
  const firstLayover = layovers?.[0]
  const firstLayoverAirport = firstLayover?.airportCode ?? firstLayover?.airportName
  const layoverDetail = firstLayover?.duration && firstLayoverAirport
    ? intl.formatMessage({
        id: 'pMwDh79Y-IXtfa-5-JrV',
        defaultMessage: '{duration} {airport}',
        description: 'Layover duration and airport displayed under the stop count on a saved flight card in the trip planner timeline',
      }, { duration: firstLayover.duration, airport: firstLayoverAirport })
    : firstLayoverAirport
  const isBooked = isBookedFlightOption(option)
  const arrivalDaysAhead = dayOffset(
    savedData.departureDate ?? savedData.departureTime,
    savedData.arrivalDate ?? savedData.arrivalTime,
  )
  const leFlightData = isLeFlightSavedItemData(savedData) ? savedData : undefined
  const totalPrice = leFlightData?.fareTotalPrice
  const hasPrice = !isBooked && totalPrice !== undefined && Number.isFinite(totalPrice) && totalPrice >= 0 && !!leFlightData?.fareCurrencyCode

  const handleOpen = useCallback(() => onOpen(option), [onOpen, option])
  const handleToggleMenu = useCallback(() => setIsMenuOpen(open => !open), [])
  const handleCloseMenu = useCallback(() => setIsMenuOpen(false), [])
  const handleDelete = useCallback(() => {
    setIsMenuOpen(false)
    onDelete(option.itemId)
  }, [onDelete, option.itemId])

  const menuLabel = intl.formatMessage({
    id: 'jeM8yDqJs__tfne6Hfpy',
    defaultMessage: 'More flight options',
    description: 'Accessible label for the saved or booked flight card menu in the trip planner timeline',
  })
  const deleteLabel = intl.formatMessage({
    id: 'tIqA2l1Ir8TxtJVcN5iz',
    defaultMessage: 'Delete',
    description: 'Dropdown menu item to delete a shortlisted flight from the trip planner',
  })
  const fromLabel = intl.formatMessage({
    id: 'nRJCghFgbNEujONdXADB',
    defaultMessage: 'From',
    description: 'Caption above the saved total flight price in the trip planner timeline',
  })

  return (
    <Card background="normal-background" border="light-foreground" borderRadius="M">
      <CardContent type="button" onClick={handleOpen}>
        <FlightSummaryRow
          carrierName={savedData.airline}
          logoSrc={logo}
          departureTime={departureTime}
          arrivalTime={arrivalTime}
          arrivalDaysAhead={arrivalDaysAhead}
          durationLabel={savedData.duration}
          routeLabel={route}
          numberOfStops={numberOfStops}
          layoverDetail={layoverDetail}
        />
        {isBooked && <BookedLabel />}
        {hasPrice && <CardPrice>
          <PriceRowPriceCaption>{fromLabel}</PriceRowPriceCaption>
          <CurrencyProvider currency={leFlightData.fareCurrencyCode}>
            <PriceRowPrice size="S" price={totalPrice} saleUnit="total" />
          </CurrencyProvider>
        </CardPrice>}
      </CardContent>
      <CardMenu>
        <IconButton
          kind="tertiary"
          size="small"
          aria-label={menuLabel}
          onClick={handleToggleMenu}
          ref={menuTriggerRef}
          disabled={disabled}
        >
          <LineEllipsisVIcon size="S" />
        </IconButton>
        <ResponsiveDropdownList
          size="S"
          open={isMenuOpen}
          onClose={handleCloseMenu}
          triggerRef={menuTriggerRef}
          anchorRef={menuTriggerRef}
          placement="bottom-end"
          portaled
        >
          <ClickableListItem title={deleteLabel} size="medium" startIcon={<LineTrashAltIcon size="S" />} onClick={handleDelete} />
        </ResponsiveDropdownList>
      </CardMenu>
    </Card>
  )
}

interface Props {
  transport: TransportConnection
  reversed: boolean
  disabled: boolean
  onDelete: (itemId: number) => void
}

function TimelineFlightsSection({ transport, reversed, disabled, onDelete }: Props) {
  const intl = useIntl()
  const [showAll, setShowAll] = useState(false)
  const options = useMemo(() => getTimelineOptions(transport, reversed), [transport, reversed])
  const { viewBookedFlightDetails, viewUnbookedFlightDetails, flightDetailsDrawer } = useViewBookedShortlistedFlight()
  const showMore = !reversed && options.length > 1 && isBookedFlightOption(options[0].option)
  const visibleOptions = showMore && !showAll ? options.slice(0, 1) : options

  const handleOpen = useCallback((option: TransportOption) => {
    if (!isBookedFlightOption(option)) {
      viewUnbookedFlightDetails(option).catch(() => undefined)
      return
    }
    const outboundData = isLeFlightSavedItemData(option.savedItemData) ? option.savedItemData : undefined
    const returnData = isLeFlightSavedItemData(option.returnLeg?.savedItemData) ? option.returnLeg.savedItemData : undefined
    const orderId = outboundData?.orderId ?? returnData?.orderId
    const orderFlightItemId = outboundData?.orderFlightItemId ?? returnData?.orderFlightItemId
    if (orderId && orderFlightItemId) {
      viewBookedFlightDetails(option)
      return
    }
    viewUnbookedFlightDetails(option).catch(() => undefined)
  }, [viewBookedFlightDetails, viewUnbookedFlightDetails])
  const handleToggleShowAll = useCallback(() => setShowAll(current => !current), [])

  if (options.length === 0) return null

  const showMoreLabel = intl.formatMessage({
    id: 'w-bY-yoBvcreknnkGJ3q',
    defaultMessage: 'Show more',
    description: 'Link to expand additional saved flights beneath a booked flight on the trip planner timeline',
  })
  const showLessLabel = intl.formatMessage({
    id: 'YDgDmgp3RxS4oMOH0R9F',
    defaultMessage: 'Show less',
    description: 'Link to collapse additional saved flights beneath a booked flight on the trip planner timeline',
  })

  return (
    <Cards>
      {visibleOptions.map(({ option, savedData }) => (
        <TimelineFlightCard
          key={option.itemId}
          option={option}
          savedData={savedData}
          disabled={disabled}
          onOpen={handleOpen}
          onDelete={onDelete}
        />
      ))}
      {showMore && <MoreLink size="caption" weight="regular" onClick={handleToggleShowAll}>{showAll ? showLessLabel : showMoreLabel}</MoreLink>}
      {flightDetailsDrawer}
    </Cards>
  )
}

export default TimelineFlightsSection
